// moduleAiBridge.js — capability-scoped AI completions for workspace MODULES
// (manifest v2). The AI-plane sibling of moduleDataBridge.js: instead of the
// records grant, the gate is the module's server-granted `capabilities.ai`
// (workspace.settings.workspaceModule.capabilities.ai — written by the
// workspace owner/admin, never by the module itself).
//
// The loader (workspaceProjects.js) registers the built bridge as the
// `moduleAiChat` context function when an `ai` grant is present, so module
// functions reach the LLM via `el.call('moduleAiChat', prompt, opts)` — never
// through raw fetch / a browser API key (the publish-time module audit forbids
// those). It reuses the existing server-routed, zero-persistence completion
// (`sdk.ai.turn` → POST /core/agents/workspaces/:wsId/turn → Anthropic),
// workspace-scoped — the key stays server-side.
//
// Returns a plain result object `{ ok, text, usage?, error? }` (never throws)
// so a module copilot degrades gracefully. `opts.systemRef` is forwarded to
// the turn call so the server can resolve a server-stored system prompt
// (B1); `usage` (inputTokens/outputTokens) passes through from the server
// response when present.
//
// STREAMING sibling — `el.call('moduleAiStream', prompt, opts)`: the SAME
// gate, the SAME request and the SAME result shape, but the answer arrives
// as it is written (`sdk.ai.turnStream` → the same /turn with stream:true →
// SSE). `opts.onText(fullText)` gets the answer so far — batched to at most
// one call per animation frame, so a module re-renders at frame rate, not per
// token. `opts.signal` aborts it like moduleAiChat. A module feature-detects
// it (`el.context.functions.moduleAiStream`) and keeps moduleAiChat as the
// fallback; on an SDK without turnStream the bridge itself falls back to the
// one-shot turn and calls onText once with the whole answer.

import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)
// Live-grant heal (cold-boot race): the loader can build these bridges before
// the workspace settings record has arrived, so the load-time `capabilities.ai`
// snapshot may be absent while the REAL grant exists — the same defect family
// moduleDataBridge heals with _healGrant. Resolve the grant at CALL time from
// the live settings, falling back to the captured snapshot. The server
// enforces the grant on every /turn regardless — this client gate is UX, not
// security, so healing it open never widens access.
const _liveAiCap = (root, snapshot) => {
  try {
    const { settings } = shellWorkspaceSettings(root)
    const cap = settings && settings.workspaceModule && settings.workspaceModule.capabilities
    if (cap && cap.ai) return cap.ai
  } catch (e) {
    // settings accessor unavailable mid-boot — snapshot decides
  }
  return snapshot || null
}

// The sdk's `ai` service, or null. Services init async and are reached via
// getService(name) (a promise); sdk.ai is not a direct property. Fall back to
// sdk.ai for any build that does expose it directly.
const _aiBridgeService = async (getSdkFn) => {
  const sdk = getSdkFn ? getSdkFn() : null
  if (!sdk) return null
  try {
    return typeof sdk.getService === 'function' ? await sdk.getService('ai') : sdk.ai
  } catch (e) {
    return sdk.ai || null
  }
}

// Workspace-scoped — the /turn route lives on the workspace.
const _aiBridgeWorkspaceId = (root) =>
  (root && root.workspace && (root.workspace.id || root.workspace._id)) || undefined

// The /turn payload — ONE builder for moduleAiChat and moduleAiStream, so the
// two can never send a different request for the same call.
const _aiBridgeTurnPayload = (prompt, options, cap) => {
  const payload =
    Array.isArray(options.messages) && options.messages.length
      ? { messages: options.messages }
      : { content: String(prompt || options.content || '') }
  const system = options.system || (cap && cap.system) || null
  if (system) payload.system = String(system)
  const modelMode = options.modelMode || (cap && cap.modelMode) || null
  if (modelMode) payload.modelMode = modelMode
  if (options.systemRef) payload.systemRef = String(options.systemRef)
  if (options.allowTools === false) payload.allowTools = false
  return payload
}

// Bellforge B10 — an optional AbortSignal from the module (a Cancel button)
// rides through sdk.ai → fetch → the server, which aborts the upstream model
// call. An aborted turn reports { ok:false, aborted:true } so the module can
// tell "user cancelled" from "the AI failed" without string-matching an
// error message.
const _aiBridgeTurnOpts = (root, options) => {
  const wsId = _aiBridgeWorkspaceId(root)
  const turnOpts = wsId ? { workspaceId: wsId } : {}
  if (options.signal) turnOpts.signal = options.signal
  return turnOpts
}

// Build the scoped AI facade for one module's capability grant. `getSdkFn` is
// injectable for tests; defaults to the shared getSDK().
export const buildModuleAiBridge = (root, capabilities = {}, { getSdkFn = shellGetSDK } = {}) => {
  const aiCap = (capabilities && capabilities.ai) || null

  return async function moduleAiChat(prompt, opts = {}) {
    const options = opts || {}
    const cap = _liveAiCap(root, aiCap)
    if (!cap) return { ok: false, text: '', error: 'AI capability not granted.' }
    const ai = await _aiBridgeService(getSdkFn)
    if (!ai || typeof ai.turn !== 'function') {
      return { ok: false, text: '', error: 'AI service unavailable.' }
    }
    const payload = _aiBridgeTurnPayload(prompt, options, cap)
    try {
      const r = await ai.turn(payload, _aiBridgeTurnOpts(root, options))
      return { ok: true, text: (r && r.text) || '', usage: (r && r.usage) || undefined }
    } catch (e) {
      if (options.signal && options.signal.aborted) {
        return { ok: false, aborted: true, text: '', error: 'Cancelled.' }
      }
      return { ok: false, text: '', error: (e && e.message) || 'AI request failed.' }
    }
  }
}

// When onText may run next: the next animation frame, raced by a timer — a
// hidden tab gets no frames at all, and a stream that stops painting there
// looks dead. Without rAF (a worker, a test) the timer alone: ~50 ms.
// Returns a cancel function.
const _aiBridgeNextFrame = (fn) => {
  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : null
  let done = false
  let frame = null
  let timer = null
  const stopBoth = () => {
    if (frame !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame)
    if (timer !== null) clearTimeout(timer)
  }
  const run = () => {
    if (done) return
    done = true
    stopBoth()
    fn()
  }
  if (raf) frame = raf(run)
  timer = setTimeout(run, raf ? 250 : 50)
  return () => {
    done = true
    stopBoth()
  }
}

// The streaming bridge — el.call('moduleAiStream', prompt, opts). Same gate
// (the live `ai` grant), same payload, same `{ ok, text, usage?, error? }`,
// and it never throws. opts: everything moduleAiChat takes, plus
//   onText(fullText) — the answer so far, at most once per animation frame,
//     and once more with the final text before the call resolves. The text
//     can restart: when the server drops words that are not the answer (a
//     tool step's "let me check…"), onText gets the new answer from its start.
// An aborted call answers { ok:false, aborted:true, partial? } and a failed
// one { ok:false, error, partial? } — `partial` is the text received before.
// `getSdkFn` / `nextFrame` are injectable for tests.
export const buildModuleAiStreamBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK, nextFrame = _aiBridgeNextFrame } = {}
) => {
  const aiCap = (capabilities && capabilities.ai) || null

  return async function moduleAiStream(prompt, opts = {}) {
    const options = opts || {}
    const cap = _liveAiCap(root, aiCap)
    if (!cap) return { ok: false, text: '', error: 'AI capability not granted.' }
    const ai = await _aiBridgeService(getSdkFn)
    const streams = !!ai && typeof ai.turnStream === 'function'
    if (!ai || (!streams && typeof ai.turn !== 'function')) {
      return { ok: false, text: '', error: 'AI service unavailable.' }
    }
    const payload = _aiBridgeTurnPayload(prompt, options, cap)
    const turnOpts = _aiBridgeTurnOpts(root, options)
    const onText = typeof options.onText === 'function' ? options.onText : null

    // onText batching — per call, so nothing outlives it: keep only the
    // LATEST full text and hand it over at most once per frame; the final
    // text goes over before the call resolves (skipped when the module
    // already has it); after a cancel or a failure the pending text is
    // dropped — nothing paints. A throwing onText never breaks the turn.
    let partial = ''
    let pendingText = null
    let sentText = null
    let cancelFrame = null
    const deliverText = () => {
      cancelFrame = null
      if (pendingText === null) return
      const text = pendingText
      pendingText = null
      if (text === sentText) return
      sentText = text
      try {
        onText(text)
      } catch (e) {
        // the module's render fault is the module's — the turn goes on
      }
    }
    const dropPendingText = () => {
      if (cancelFrame) cancelFrame()
      cancelFrame = null
      pendingText = null
    }

    try {
      // An SDK without turnStream gets the one-shot turn; onText then fires
      // once, with the whole answer (the flush below).
      const r = streams
        ? await ai.turnStream(payload, {
            ...turnOpts,
            onDelta: (_delta, fullText) => {
              partial = String(fullText || '')
              if (!onText) return
              pendingText = partial
              if (!cancelFrame) cancelFrame = nextFrame(deliverText)
            }
          })
        : await ai.turn(payload, turnOpts)
      const text = (r && r.text) || ''
      if (onText) {
        dropPendingText()
        pendingText = text
        deliverText()
      }
      return { ok: true, text, usage: (r && r.usage) || undefined }
    } catch (e) {
      dropPendingText()
      const rest = partial ? { partial } : {}
      if (options.signal && options.signal.aborted) {
        return { ok: false, aborted: true, text: '', error: 'Cancelled.', ...rest }
      }
      return { ok: false, text: '', error: (e && e.message) || 'AI request failed.', ...rest }
    }
  }
}

// Admin AI-ops bridge (B7) — `el.call('moduleAiAdmin', op, args)`:
//   'usage'    → GET the workspace's monthly AI usage (sdk.ai.usage)
//   'settings' → merge-write workspace.settings.ai ({ modelMode?,
//                monthlyTokenCap? }); the server enforces the admin role.
// Gated on the same `ai` capability grant as moduleAiChat. Returns
// { ok, data?, error? } and never throws.
export const buildModuleAiAdminBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK } = {}
) => {
  const aiCap = (capabilities && capabilities.ai) || null
  return async function moduleAiAdmin(op, args = {}) {
    if (!_liveAiCap(root, aiCap)) return { ok: false, error: 'AI capability not granted.' }
    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk) return { ok: false, error: 'AI service unavailable.' }
    const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || undefined
    try {
      if (op === 'usage') {
        let ai
        try {
          ai = typeof sdk.getService === 'function' ? await sdk.getService('ai') : sdk.ai
        } catch (e) {
          ai = sdk.ai || null
        }
        if (!ai || typeof ai.usage !== 'function') {
          return { ok: false, error: 'AI usage unavailable.' }
        }
        const data = await ai.usage(wsId ? { workspaceId: wsId } : {})
        return { ok: true, data }
      }
      if (op === 'credits') {
        // The AUTHORITATIVE workspace credit balance (server
        // CreditsService.getWorkspaceBalance via sdk.getCreditBalance —
        // pool-scope aware, member-gated server-side). Exposed on the SAME
        // ai grant as 'usage': the grant that spends credits grants reading
        // the spend. Added for the bellforge credits ticket (2026-08-15):
        // the app's hand-seeded bf_credits row is not a money surface, and
        // a module has no other route to the real ledger (el.sdk is
        // refused by the publish-time module audit).
        if (!wsId) return { ok: false, error: 'No workspace selected.' }
        if (typeof sdk.getCreditBalance !== 'function') {
          return { ok: false, error: 'Credits service unavailable.' }
        }
        const data = await sdk.getCreditBalance(wsId)
        return { ok: true, data }
      }
      if (op === 'settings') {
        if (!wsId) return { ok: false, error: 'No workspace selected.' }
        // WRITE-BASE (spec §5). Not a DECISION site: `cur` is never branched
        // on, it is spread into the patch that gets PUT back. The server merges
        // per top-level key, so `{ ai: patch }` REPLACES the whole `ai` object
        // — a `{}` base read from a record that simply had not arrived writes
        // back an `ai` bag missing whichever of `modelMode` / `monthlyTokenCap`
        // this call did not set, silently erasing the workspace's cap.
        //
        // Aborting is the only safe answer here (there is no per-leaf half of
        // this write to keep, unlike the featureFlags/voice setters), and this
        // function already speaks `{ ok, error }`, so the caller gets a real
        // failure instead of a success that quietly lost a setting.
        const { settings, ready } = shellWorkspaceSettings(root)
        if (!ready) {
          return { ok: false, error: 'Workspace settings are still loading — try again shortly.' }
        }
        const cur = (settings && settings.ai) || {}
        const patch = { ...cur }
        if (args.modelMode !== undefined) patch.modelMode = args.modelMode
        if (args.monthlyTokenCap !== undefined) patch.monthlyTokenCap = args.monthlyTokenCap
        await sdk.updateWorkspaceSettings(wsId, { ai: patch })
        return { ok: true, data: patch }
      }
      return { ok: false, error: 'Unknown op: ' + op }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'AI admin request failed.' }
    }
  }
}
